import React, { useState } from 'react';
import { 
  LayoutDashboard, Sparkles, CheckSquare, FileText, Activity,
  Clock, Calendar, GitBranch, Users, Bot, FolderTree,
  MessagesSquare, Shield, ShieldCheck, ChevronDown, ChevronRight,
  Plus, Upload, Timer, MoreHorizontal, Mail, ArrowLeft
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

/**
 * NavItem - Quiet dark sidebar navigation with breathing room
 */
const NavItem = ({ item, isActive, collapsed, onTabChange }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={() => onTabChange(item.id)}
      className={`
        group w-full flex items-center gap-3 px-2.5 py-2.5 rounded-md
        transition-all duration-150
        ${isActive 
          ? 'bg-white/[0.06] text-white/85 border-l-[2px] border-white/30 -ml-0.5 pl-2' 
          : 'text-white/30 hover:text-white/55 hover:bg-white/[0.02]'
        }
      `}
    >
      <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${isActive ? 'text-white/80' : 'text-white/25'}`} strokeWidth={1.5} />
      
      {!collapsed && (
        <>
          <span className={`flex-1 text-left text-[13px] tracking-wide ${isActive ? 'font-medium' : 'font-normal'}`}>{item.label}</span>
          
          {item.count > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-white/[0.08] text-white/60">
              {item.count > 99 ? '99+' : item.count}
            </span>
          )}
        </>
      )}
    </button>
  );
};

/**
 * CaseSidebarNav - Dashboard-matching dark sidebar design
 */
const CaseSidebarNav = ({
  caseData,
  activeTab,
  onTabChange,
  taskCount = 0,
  unreadActivityCount = 0,
  newDocumentCount = 0,
  collapsed = false,
  onToggleCollapse
}) => {
  const { t } = useTranslation('caseWorkspace');
  const navigate = useNavigate();
  const [secondaryExpanded, setSecondaryExpanded] = useState(true);
  
  // Status config - Minimal badges
  const getStatusConfig = (status) => {
    const configs = {
      'NEW': { label: t('status.new') },
      'IN_PROGRESS': { label: t('status.active') },
      'WAITING_CLIENT': { label: t('status.waitingClient') },
      'WAITING_COUNTERPART': { label: t('status.waitingCounterpart') },
      'COMPLETED': { label: t('status.completed') },
      'ARCHIVED': { label: t('status.archived') }
    };
    return configs[status] || configs['IN_PROGRESS'];
  };

  // Primary nav items - Core workflow items
  const primaryItems = [
    { id: 'overview', icon: LayoutDashboard, label: t('sidebar.overview') },
    { id: 'chat', icon: Mail, label: t('sidebar.messages') },
    { id: 'ai-workspace', icon: Sparkles, label: t('sidebar.aiWorkspace') },
    { id: 'documents', icon: FileText, label: t('sidebar.documents'), count: newDocumentCount },
    { id: 'tasks', icon: CheckSquare, label: t('sidebar.tasks'), count: taskCount },
    { id: 'activity', icon: Activity, label: t('sidebar.activity'), count: unreadActivityCount },
    { id: 'time', icon: Clock, label: t('sidebar.time') },
  ];

  // Secondary nav items - Planning & Team management
  const secondaryItems = [
    { id: 'collaboration', icon: Users, label: t('sidebar.resources') },
    { id: 'plan', icon: Calendar, label: t('sidebar.plan') },
    { id: 'phases', icon: GitBranch, label: t('sidebar.phases') },
    { id: 'team-chat', icon: MessagesSquare, label: t('sidebar.teamChat') },
    { id: 'subcases', icon: FolderTree, label: t('sidebar.subcases') },
  ];

  // Tool items
  const toolItems = [
    { id: 'agents', icon: Bot, label: t('sidebar.aiAgents') },
    { id: 'conflicts', icon: Shield, label: t('sidebar.conflicts') },
    ...(caseData?.requires_kyc ? [{ id: 'kyc', icon: ShieldCheck, label: t('sidebar.kyc') }] : []),
  ];

  const statusConfig = getStatusConfig(caseData?.status);

  return (
    <div className={`
      h-full flex flex-col bg-[#141414]
      transition-all duration-300
      ${collapsed ? 'w-14' : 'w-56'}
    `}>
      {/* Back Button */}
      <div className="flex-shrink-0 px-3 py-3">
        <button
          onClick={() => navigate('/lexy/dashboard')}
          className="flex items-center gap-2 text-white/40 hover:text-white/65 text-[13px] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {!collapsed && <span>Tilbake</span>}
        </button>
      </div>

      {/* Case Header */}
      <div className="flex-shrink-0 px-4 pb-4 border-b border-white/[0.06]">
        {collapsed ? (
          <div className="w-9 h-9 bg-white/[0.08] rounded-lg flex items-center justify-center mx-auto">
            <span className="text-white/70 text-sm font-medium">
              {caseData?.topic?.charAt(0)?.toUpperCase() || 'S'}
            </span>
          </div>
        ) : (
          <div>
            <h2 className="font-medium text-white/90 text-[15px] leading-tight mb-1 tracking-tight">
              {caseData?.topic || t('sidebar.untitled')}
            </h2>
            
            {caseData?.client_name && (
              <p className="text-[13px] text-white/40 mb-3">
                {caseData.client_name}
              </p>
            )}
            
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/80"></span>
              <span className="text-[11px] text-white/45">{statusConfig.label}</span>
            </div>
          </div>
        )}
      </div>

      {/* Navigation with breathing room */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <div className="space-y-1">
          {primaryItems.map(item => (
            <NavItem 
              key={item.id} 
              item={item} 
              isActive={activeTab === item.id}
              collapsed={collapsed}
              onTabChange={onTabChange}
            />
          ))}
        </div>

        {/* Secondary section - with extra top spacing */}
        <div className="mt-6">
          {!collapsed && (
            <button
              onClick={() => setSecondaryExpanded(!secondaryExpanded)}
              className="w-full flex items-center gap-2 px-2.5 py-2 text-[10px] font-medium text-white/25 uppercase tracking-widest hover:text-white/45 transition-colors"
            >
              {secondaryExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
              {t('sidebar.more')}
            </button>
          )}
          
          <AnimatePresence>
            {(collapsed || secondaryExpanded) && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="space-y-0.5 overflow-hidden"
              >
                {secondaryItems.map(item => (
                  <NavItem 
                    key={item.id} 
                    item={item} 
                    isActive={activeTab === item.id}
                    collapsed={collapsed}
                    onTabChange={onTabChange}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Tools section - tighter spacing */}
        {toolItems.length > 0 && (
          <div className="mt-6 pt-4 border-t border-white/[0.04]">
            {!collapsed && (
              <div className="px-2.5 py-1.5 text-[10px] font-medium text-white/25 uppercase tracking-widest">
                {t('sidebar.tools')}
              </div>
            )}
            <div className="space-y-0.5">
              {toolItems.map(item => (
                <NavItem 
                  key={item.id} 
                  item={item} 
                  isActive={activeTab === item.id}
                  collapsed={collapsed}
                  onTabChange={onTabChange}
                />
              ))}
            </div>
          </div>
        )}
      </nav>

      {/* Collapse toggle */}
      {onToggleCollapse && (
        <div className="flex-shrink-0 p-2 border-t border-white/[0.06]">
          <button
            onClick={onToggleCollapse}
            className="w-full flex items-center justify-center gap-2 px-2.5 py-2 text-[11px] text-white/30 hover:text-white/50 hover:bg-white/[0.03] rounded-md transition-colors"
          >
            <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
            {!collapsed && <span>{t('sidebar.hide')}</span>}
          </button>
        </div>
      )}
    </div>
  );
};

export default CaseSidebarNav;
